"use client";
import { Button } from "@workspace/ui/components/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@workspace/ui/components/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { Separator } from "@workspace/ui/components/separator";
import { VisuallyHidden } from "@workspace/ui/components/visually-hidden";
import { useIsMobile } from "@workspace/ui/hooks/use-mobile";
import { cn } from "@workspace/ui/lib/utils";
import { X } from "lucide-react";
import { useCallback, useState } from "react";
import { ListingRow } from "./listing-row";
import type { JobClusterFeature, JobClusterLeaf } from "./use-cluster";

type ClusterMarkerProps = {
  cluster: JobClusterFeature;
  onSelectJob: (cluster: JobClusterLeaf) => void;
  getClusterLeaves: (
    clusterId: number,
    limit?: number,
    offset?: number
  ) => JobClusterLeaf[];
};

function getClusterDiameter(count: number) {
  if (count >= 100) {
    return 64;
  }
  if (count >= 50) {
    return 56;
  }
  if (count >= 20) {
    return 48;
  }
  if (count >= 10) {
    return 40;
  }
  return 32;
}

function getClusterSizeClass(count: number) {
  if (count >= 100) {
    return "size-16";
  }
  if (count >= 50) {
    return "size-14";
  }
  if (count >= 20) {
    return "size-12";
  }
  if (count >= 10) {
    return "size-10";
  }
  return "size-8";
}

function getClusterPopoverSideOffset({
  pointCount,
  rowCount,
  hasFooter,
}: {
  pointCount: number;
  rowCount: number;
  hasFooter: boolean;
}) {
  const estimatedHeaderHeight = 64;
  const estimatedRowHeight = 100;
  const estimatedFooterHeight = hasFooter ? 32 : 0;
  const visibleRows = Math.max(1, Math.min(rowCount, 4));
  const estimatedPopoverHeight =
    estimatedHeaderHeight +
    visibleRows * estimatedRowHeight +
    estimatedFooterHeight;

  return -Math.round(
    (estimatedPopoverHeight + getClusterDiameter(pointCount)) / 2
  );
}

export function ClusterMarker({
  cluster,
  onSelectJob,
  getClusterLeaves,
}: ClusterMarkerProps) {
  const { point_count: pointCount, cluster_id: clusterId } = cluster.properties;
  const [leaves, setLeaves] = useState<JobClusterLeaf[] | null>(null);
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (newOpen && !leaves && getClusterLeaves) {
        const result = getClusterLeaves(clusterId, 50);
        setLeaves(result);
      }
    },
    [leaves, getClusterLeaves, clusterId]
  );

  const handleSelectJob = useCallback(
    (leaf: JobClusterLeaf) => {
      setOpen(false);
      window.setTimeout(() => onSelectJob(leaf), 0);
    },
    [onSelectJob]
  );

  const isMobile = useIsMobile();

  const trigger = (
    <button
      className={cn(
        "hover:-translate-y-2 flex cursor-pointer items-center justify-center rounded-full border-2 border-foreground/20 bg-foreground/20 shadow-lg transition-[transform,box-shadow] duration-300 hover:shadow-2xl",
        getClusterSizeClass(pointCount)
      )}
      type="button"
    >
      <span className="font-bold font-mono text-foreground tabular-nums">
        {pointCount >= 1000 ? `${(pointCount / 1000).toFixed(1)}k` : pointCount}
      </span>
    </button>
  );

  const clusterHeader = (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        <p className="text-balance font-semibold text-sm leading-5">
          {pointCount} {pointCount === 1 ? "listing" : "listings"} in this area
        </p>
        <p className="mt-0.5 text-muted-foreground text-xs leading-4">
          Select one to view details
        </p>
      </div>
      <button
        aria-label="Close cluster listings"
        className="flex size-10 shrink-0 appearance-none items-center justify-center rounded-md border-0 bg-transparent p-0 text-muted-foreground transition-[background-color,color,scale] hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 active:scale-[0.96]"
        onClick={() => setOpen(false)}
        type="button"
      >
        <X className="size-4" />
      </button>
    </div>
  );

  const clusterListings =
    leaves && leaves.length > 0 ? (
      <ClusterJobList leaves={leaves} onSelectJob={handleSelectJob} />
    ) : (
      <div className="px-4 py-8 text-center text-muted-foreground text-sm">
        Loading listings...
      </div>
    );

  const clusterMoreFooter =
    leaves && pointCount > leaves.length ? (
      <div className="border-border/70 border-t bg-muted/30 px-4 py-2.5 text-center text-muted-foreground text-xs">
        +{pointCount - leaves.length} more{" "}
        {pointCount - leaves.length === 1 ? "listing" : "listings"}
      </div>
    ) : null;

  const body = (
    <>
      {clusterHeader}
      <Separator />
      {leaves && leaves.length > 0 ? (
        leaves.length > 3 ? (
          <ScrollArea className="h-[26rem] max-h-[58vh]">
            <ClusterJobList leaves={leaves} onSelectJob={handleSelectJob} />
          </ScrollArea>
        ) : (
          <ClusterJobList leaves={leaves} onSelectJob={handleSelectJob} />
        )
      ) : (
        <div className="px-4 py-8 text-center text-muted-foreground text-sm">
          Loading listings...
        </div>
      )}
      {clusterMoreFooter}
    </>
  );

  if (isMobile) {
    return (
      <Drawer onOpenChange={handleOpenChange} open={open}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="max-h-[calc(100dvh-4rem)]">
          <VisuallyHidden>
            <DrawerHeader>
              <DrawerTitle>
                {pointCount} {pointCount === 1 ? "listing" : "listings"} in this
                area
              </DrawerTitle>
              <DrawerDescription>Select one to view details</DrawerDescription>
            </DrawerHeader>
          </VisuallyHidden>
          {clusterHeader}
          <Separator />
          <div className="min-h-0 flex-1 overflow-y-auto">
            {clusterListings}
            {clusterMoreFooter}
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button className="w-full" variant="outline">
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-96 overflow-hidden rounded-xl border-border/80 p-0 shadow-2xl"
        side="bottom"
        sideOffset={getClusterPopoverSideOffset({
          pointCount,
          rowCount: leaves?.length ?? pointCount,
          hasFooter: !!leaves && pointCount > leaves.length,
        })}
      >
        {body}
      </PopoverContent>
    </Popover>
  );
}

function ClusterJobList({
  leaves,
  onSelectJob,
}: {
  leaves: JobClusterLeaf[];
  onSelectJob: (leaf: JobClusterLeaf) => void;
}) {
  return (
    <div className="divide-y divide-border/70">
      {leaves.map((leaf) => (
        <ListingRow
          idPrefix="cluster-listing"
          job={leaf.properties.job}
          key={leaf.properties.id}
          onSelect={() => onSelectJob(leaf)}
        />
      ))}
    </div>
  );
}
